import { useEffect } from 'react';

import { billingApi, BusinessSubscriptionModel, profileApi, ProfileModel } from '@pukka/api';
import { dateUtils, numberUtils, animationUtil } from '@pukka/common/utils';
import { Channel, pusher } from '@pukka/pusher';
import { queryClient } from '@pukka/tanstack-query';
import { toast } from '@pukka/toast';
import { proxy, withPersist } from '@pukka/valtio';
import capitalize from 'lodash/capitalize';
import noop from 'lodash/noop';

import { createDiscountDescriptionFromCoupon, getIntervalFromMonth, getRecurringDiscountDescription } from './utils';

type InvoiceStatus = 'paid' | 'failed' | 'success' | 'pending';

type DataStatus = 'error' | 'success' | 'loading' | 'unloaded';

type PaymentStatus = 'failed' | 'success' | 'pending' | 'processing';

interface BillingHistory {
  readonly number: string;
  readonly date: string;
  readonly period: string;
  readonly amount: number;
  readonly amountFormatted: string;
  readonly status: InvoiceStatus;
  readonly url: string;
}

interface Outlet {
  readonly id: number;
  readonly name: string;
  readonly expiry: string;
}

interface IBillingState extends Record<string, unknown> {
  isPayingRecurring: boolean;
  paymentStatus?: PaymentStatus;
  status: {
    profile: DataStatus;
    histories: DataStatus;
    recurring: DataStatus;
    regular: DataStatus;
  };
  calculations: BusinessSubscriptionModel.BillingCalculation[];
  channel: Channel | null;
  readonly isRecurringFetching: boolean;
  onChangeIsPayingRecurring(value: boolean): void;
  onConsumePayingStatus(interval?: string): void;
  pollingActiveRecurring(): void;
  useBillingData(): void;
}

interface IBillingPersistedState extends Record<string, unknown> {
  isRecurring: boolean;
  pendingPaymentMessage: string;
  profile: {
    isOwner: boolean;
    businessId: number;
    expiredAt: string;
    hasExpired: boolean;
  } | null;
  histories: BillingHistory[];
  recurring: {
    plan: string;
    description: string;
    interval: 'yearly' | 'monthly';
    discountBanner: string;
    nextBillingDate: string;
    outletCount: number;
    employeeSlotCount: number;
    errorMessage: string;
    payment: {
      type: 'credit_card' | 'debit';
      cardName: string;
      cardBillingTo: string;
      cardImageUrl: string;
      title: string;
      caption: string;
    };
    prices: {
      coupon: {
        amount: number;
        code: string;
        percentage: number;
        description: string;
      };
      outlets: {
        originalPrice: number;
        finalPrice: number;
        unit: string;
        description?: string;
        prorateInfo?: string;
      };
      employeeSlots: {
        originalPrice: number;
        finalPrice: number;
        unit: string;
      };
      total: {
        originalPrice: number;
        finalPrice: number;
        discount: number;
        description?: string;
      };
    };
  } | null;
  regular: {
    plan: string;
    lastPaymentDate: string;
    employeeSlotCount: number;
    outlets: Outlet[];
  } | null;
}

function onPushed(BillingState: IBillingState) {
  return () => {
    if (!BillingState.paymentStatus) {
      BillingState.paymentStatus = 'success';

      BillingState.onConsumePayingStatus();
    }
  };
}

function getCurrentPlanName(currentSubscriptionPlanName: string, business?: ProfileModel.Business) {
  const {
    is_trial: isTrial = false,
    has_expired: hasExpired = false,
    is_enterprise: isEnterprise = false,
    is_mokapos_user: isMokaposUser = true,
  } = business || {};

  if (hasExpired) {
    return 'Expired';
  }

  if (isEnterprise) {
    return 'Moka Enterprise';
  }

  if (!isMokaposUser) {
    return 'MokaPay';
  }

  if (isTrial) {
    return 'Moka POS Trial';
  }

  return currentSubscriptionPlanName;
}

const { proxy: BillingPersistedState } = withPersist<IBillingPersistedState>(
  {
    isRecurring: false,
    pendingPaymentMessage: '',
    histories: [],
    profile: null,
    recurring: null,
    regular: null,
  },
  'billing',
  {
    expiry: 1000 * 60 * 60 * 24, // 1 day expiry
    session: 'user', // removed on logout
  },
);

const BillingState: IBillingState = proxy({
  isPayingRecurring: false,
  paymentStatus: undefined,
  status: {
    profile: 'unloaded',
    histories: 'unloaded',
    recurring: 'unloaded',
    regular: 'unloaded',
  },
  channel: null,
  calculations: [],
  get isRecurringFetching() {
    return BillingState.status.recurring === 'loading';
  },
  onChangeIsPayingRecurring(value) {
    animationUtil.doTransition(() => {
      BillingState.isPayingRecurring = value;
    });
  },
  onConsumePayingStatus(interval?: string) {
    const intervalText = interval ? ` ${capitalize(interval)}` : '';

    switch (BillingState.paymentStatus) {
      case 'failed':
        toast.error({
          title: `Error payment switched to Recurring${intervalText}`,
          children: 'Please check your card and try again',
        });
        break;
      case 'success':
        toast.success(`Payment switched to Recurring${intervalText} successfuly`);
        break;
      /* istanbul ignore next */
      case 'pending':
        // TODO: for now, do nothing and consume
        break;
      default:
        break;
    }

    if (BillingState.paymentStatus) {
      BillingState.paymentStatus = undefined;
    }
  },
  pollingActiveRecurring() {
    // polling for 2 minutes (120 seconds) with interval of 10 seconds
    const maxTime = 12;
    const intervalTime = 10;
    let n = 0;

    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['billing-active-recurring'] });
      n += 1;

      if (n >= maxTime || BillingState.paymentStatus === 'success') {
        if (BillingState.paymentStatus !== 'success') {
          BillingState.paymentStatus = 'failed';
          this.onConsumePayingStatus();
        }

        clearInterval(interval);
      }
    }, intervalTime * 1000);
  },
  useBillingData() {
    const { data: profile, status: profileStatus } = profileApi.useProfile();
    const { data: histories, status: historyStatus } = billingApi.useBillingHistory();
    const { data: billingsCalculationsData, status: regularStatus } = billingApi.useBillingsCalculations(profile?.business?.id);
    const { data: activeRecurring, status: recurringStatus } = billingApi.useActiveRecurringPayment();

    useEffect(() => {
      if (profile?.business?.id) {
        const businessId = profile.business.id;
        const eventName = 'bind';

        if (BillingState.channel) {
          BillingState.channel.unsubscribe();
        }

        BillingState.channel = pusher.subscribe(`recurring-payment-${businessId}`);
        BillingState.channel.bind(eventName, onPushed(BillingState));

        return () => {
          BillingState.channel?.unbind(eventName);
          BillingState.channel?.unsubscribe();
          BillingState.channel = null;
        };
      }

      return noop;
    }, [profile?.business?.id]);

    useEffect(() => {
      BillingState.status.profile = profileStatus;

      if (profileStatus === 'success') {
        BillingPersistedState.profile = {
          businessId: profile?.business?.id ?? -1,
          isOwner: profile?.is_owner ?? false,
          expiredAt: profile.business?.expired_at ?? '',
          hasExpired: profile.business?.has_expired ?? false,
        };
      }
    }, [profile, profileStatus]);

    useEffect(() => {
      BillingState.status.regular = regularStatus;

      if (regularStatus === 'success' && profileStatus === 'success') {
        const currentPlan = profile?.business?.current_plan ?? 1;
        const regularBillingCalculations = billingsCalculationsData.filter((calc) => !calc.is_recurring);
        const defaultBillingCalculation = regularBillingCalculations[0];
        const currentBillingCalculation = regularBillingCalculations.find((calc) => calc.month === currentPlan); // regular plan
        const currentSubscriptionPlanName = currentBillingCalculation?.subscription_name ?? '';

        BillingState.calculations = billingsCalculationsData;
        BillingPersistedState.regular = {
          plan: getCurrentPlanName(currentSubscriptionPlanName, profile.business),
          lastPaymentDate: profile?.business?.last_payment_date ?? '',
          // note: since every calculation has the same outlets and employee slots, it's ok to use any calculation
          employeeSlotCount: (currentBillingCalculation ?? defaultBillingCalculation)?.employee_slot?.count ?? 0,
          outlets:
            (currentBillingCalculation ?? defaultBillingCalculation)?.outlets?.map((outlet) => ({
              id: outlet.outlet_id ?? -1,
              name: outlet.outlet_name ?? '',
              expiry: dateUtils.formatDate(outlet.current_end_date),
            })) ?? [],
        };
      }
    }, [regularStatus, billingsCalculationsData, profile, profileStatus]);

    useEffect(() => {
      BillingState.status.histories = historyStatus;

      if (historyStatus === 'success') {
        BillingPersistedState.histories = histories.map((history) => ({
          amount: history.total_amount,
          amountFormatted: `Rp ${numberUtils.formatCurrency(history.total_amount)}`,
          date: dateUtils.formatDate(history.created_at),
          period: `${dateUtils.formatDate(history.start_period, 'dd MMM yyyy')} - ${dateUtils.formatDate(
            history.end_period,
            'dd MMM yyyy',
          )}`,
          number: history.invoice_number,
          status: history.status as InvoiceStatus,
          url: `${billingApi.BILLINGS_URL}/${history.invoice_number}.pdf`,
        }));

        const pendingPayment = histories
          .map((history) => {
            if (history.status === 'pending') {
              if (history.payment_type === 'credit_card') {
                return `There is a pending payment for ${history.invoice_number}. Please wait 24 hours before making a new payment.`;
              }

              if (history.payment_type === 'bank_transfer' || history.payment_type === 'echannel') {
                return `There is a pending payment for ${history.invoice_number}. Please transfer full amount of the invoice within 3 days before making a new payment. Please check your email for payment instruction.`;
              }
            }

            return false;
          })
          .filter((m) => !!m)
          .shift();

        BillingPersistedState.pendingPaymentMessage = pendingPayment || '';
      }
    }, [historyStatus, histories]);

    useEffect(() => {
      BillingState.status.recurring = recurringStatus;

      if (recurringStatus === 'success' && regularStatus === 'success') {
        if (!activeRecurring) {
          BillingPersistedState.isRecurring = false;

          BillingState.onConsumePayingStatus();

          return;
        }

        if (activeRecurring.status === 'PROCESSING') {
          if (BillingState.paymentStatus !== 'processing') {
            BillingPersistedState.isRecurring = false;
            BillingState.paymentStatus = 'processing';
            BillingState.pollingActiveRecurring();
          }

          return;
        }

        const planName = activeRecurring.package_deal_display_name ?? 'Moka Basic';
        const month = activeRecurring.month ?? 1;
        const interval = getIntervalFromMonth(month);
        const couponCode = activeRecurring.coupon_code ?? '';
        const couponPercentage = activeRecurring.coupon_discount_percentage ?? 0;
        const totalPercentage = activeRecurring.discount_percentage ?? 0;
        const paymentType = (activeRecurring.payment_type as 'credit_card') ?? 'debit';
        const cardName = activeRecurring.card_name ?? '';
        const failedPaymentCount = activeRecurring.failed_payment_count ?? 0;
        const failedPaymentMessage =
          activeRecurring.failed_payment_message ?? 'There is an issue with your payment detail. Please update your payment information.';

        if (interval === '') {
          throw new Error('Interval not found');
        }

        BillingPersistedState.recurring = {
          employeeSlotCount: activeRecurring.employee_slot_count ?? 0,
          outletCount: activeRecurring.outlet_count ?? 0,
          nextBillingDate: dateUtils.formatDate(activeRecurring.next_billing_date),
          discountBanner: ([] as string[])
            .concat(totalPercentage ? `${totalPercentage}% discount from subscribing Moka Recurring ${capitalize(interval)}` : [])
            .concat(couponPercentage ? `${couponPercentage}% from ${couponCode} coupon discount` : [])
            .join(' and '),
          interval,
          plan: planName,
          errorMessage: failedPaymentCount > 0 ? failedPaymentMessage : '',
          // TODO: removed for now, no answer from the PM
          description: '', // e.g Paket dasar usaha
          payment: {
            type: paymentType,
            cardName,
            title:
              paymentType === 'credit_card'
                ? `${activeRecurring.card_name} ending in ${activeRecurring.card_token}`
                : `Direct debit card ending in ${activeRecurring.card_token}`,
            caption: paymentType === 'credit_card' ? '' : cardName,
            cardBillingTo: `Billing to: ${activeRecurring.billing_email}`,
            cardImageUrl: activeRecurring.card_image_url ?? '',
          },
          prices: {
            coupon: {
              amount: activeRecurring.coupon_total_amount ?? 0,
              percentage: couponPercentage,
              code: couponCode,
              description: createDiscountDescriptionFromCoupon(activeRecurring.coupon_discount_percentage, undefined, couponCode),
            },
            employeeSlots: {
              originalPrice: activeRecurring.employee_slot_price_per_month?.amount ?? 0,
              finalPrice: activeRecurring.employee_slot_price_per_month?.finalAmount ?? 0,
              unit: '/slot/mo',
            },
            outlets: {
              originalPrice: activeRecurring.package_price_per_month?.amount ?? 0,
              finalPrice: activeRecurring.package_price_per_month?.finalAmount ?? 0,
              unit: '/outlet/mo',
              description: planName,
            },
            total: {
              originalPrice: (activeRecurring.original_amount as unknown as number) ?? 0,
              finalPrice: (activeRecurring.final_amount as unknown as number) ?? 0,
              discount: totalPercentage,
              description: getRecurringDiscountDescription(interval, activeRecurring.discount_percentage),
            },
          },
        };

        BillingPersistedState.isRecurring = true;

        BillingState.onConsumePayingStatus(interval);
      }

      if (recurringStatus === 'error') {
        BillingPersistedState.isRecurring = false;

        BillingState.onConsumePayingStatus();
      }
    }, [billingsCalculationsData, activeRecurring, recurringStatus, regularStatus]);
  },
});

export {
  getCurrentPlanName,
  onPushed as _onPushed,
  BillingPersistedState,
  BillingState,
  type IBillingPersistedState,
  type IBillingState,
  type Outlet,
  type BillingHistory,
  type InvoiceStatus,
  type PaymentStatus,
};
